// src/services/auth/authSession.ts
import { supabase } from '@/lib/supabase';
import * as Sentry from '@sentry/react';

type SessionDeps = {
    setUser: (u: any | null) => void;
    setOrganization: (o: any | null) => void;
    setIsAuthenticated: (v: boolean) => void;
    setIsPaid: (v: boolean) => void;
    setSubscriptionPlan: (plan: string | null) => void;
    setLoading: (v: boolean) => void;
    setSubscriptionLoading: (v: boolean) => void;
};

type SubscriptionDeps = {
    setIsPaid: (v: boolean) => void;
    setSubscriptionPlan: (plan: string | null) => void;
    setSubscriptionLoading: (v: boolean) => void;
};

export const checkSubscriptionStatusService = async (
    shopId: string | number | null | undefined,
    { setIsPaid, setSubscriptionPlan, setSubscriptionLoading }: SubscriptionDeps,
): Promise<void> => {
    // 1. Se non c'è shopId, l'utente non può aver pagato
    if (!shopId) {
        console.warn("[Auth] checkSubscription: Shop ID mancante");
        setIsPaid(false);
        setSubscriptionPlan(null);
        return;
    }

    try {
        setSubscriptionLoading(true);

        // 2. Controlla lo stato dell'abbonamento
        // Se il record non esiste, restituisce data: null senza lanciare eccezioni.
        const { data: subscription, error } = await supabase
            .from('Subscriptions')
            .select('*')
            .eq('shop_id', shopId)
            .maybeSingle();

        // 3. Gestione errori DB (es. problemi di connessione)
        if (error) {
            console.error("[Auth] Errore DB abbonamento:", error);
            setIsPaid(false);
            setSubscriptionPlan(null);
            return;
        }

        // 4. Se il record non esiste (data è null), l'utente NON è pagante.
        if (!subscription) {
            console.warn(`[Auth] Nessun abbonamento trovato per shop ${shopId}. Accesso negato.`);
            setIsPaid(false);
            setSubscriptionPlan(null);
            return;
        }

        // 5. Verifica validità abbonamento
        const isActive =
            subscription.status === 'active' || subscription.status === 'trialing';

        // Confronto date sicuro
        const endDate = new Date(subscription.current_period_end);
        const now = new Date();
        const isNotExpired = endDate > now;

        if (!isActive || !isNotExpired) {
            console.warn(`[Auth] Abbonamento non valido. Status: ${subscription.status}, Scadenza: ${endDate}`);
        }

        const paid = isActive && isNotExpired;
        setIsPaid(paid);

        let plan = null;
        if (paid && subscription.lookup_key) {
            plan = subscription.lookup_key.split('_')[1] || null;
        }
        setSubscriptionPlan(plan);

    } catch (err) {
        Sentry.captureException(err, {
            tags: { feature: 'session', action: 'check_subscription' },
            extra: { shopId }
        });
        console.error('Eccezione critica controllo sottoscrizione:', err);
        // Fail-safe: in caso di crash, nega l'accesso
        setIsPaid(false);
        setSubscriptionPlan(null);
    } finally {
        setSubscriptionLoading(false);
    }
};

export const loadInitialSession = async ({
    setUser,
    setOrganization,
    setIsAuthenticated,
    setIsPaid,
    setSubscriptionPlan,
    setLoading,
    setSubscriptionLoading,
}: SessionDeps): Promise<void> => {
    try {
        setLoading(true);

        // 1. Recupera la sessione
        const {
            data: { session },
        } = await supabase.auth.getSession();

        if (!session?.user) {
            console.log('[Auth] Nessuna sessione attiva');
            setUser(null);
            setOrganization(null);
            setIsAuthenticated(false);
            setIsPaid(false);
            setSubscriptionPlan(null);
            return;
        }

        // 2. Recupera l'Hairdresser
        const { data: hairdresser, error: hairdresserError } = await supabase
            .from('Hairdressers')
            .select('*')
            .eq('id', session.user.id)
            .maybeSingle();

        if (hairdresserError) {
            console.error("[Auth] Errore recupero Hairdresser:", hairdresserError);
            await supabase.auth.signOut();
            throw new Error("Errore nel controllo dell'utente");
        }

        // Caso: Utente Auth esiste, ma non ha record Hairdresser (es. registrazione interrotta)
        if (!hairdresser) {
            console.log(
                '[Auth] Utente autenticato senza Hairdresser - reindirizzamento a new-shop',
            );
            setUser(session.user);
            setOrganization(null);
            setIsAuthenticated(true);
            setIsPaid(false);
            setSubscriptionPlan(null);
            return;
        }

        // 3. Recupera lo Shop
        const { data: shop, error: shopError } = await supabase
            .from('Shops')
            .select('*')
            .eq('id', hairdresser.shop_id)
            .single(); // Qui usiamo single() perché un Hairdresser DEVE avere uno shop

        if (shopError || !shop) {
            console.error("[Auth] Hairdresser orfano di shop");
            await supabase.auth.signOut();
            throw new Error('Errore nel caricamento dei dati del negozio');
        }

        console.log('[Auth] Sessione caricata con successo');
        setUser(hairdresser);
        setOrganization(shop);
        setIsAuthenticated(true);

        // 4. Attendere la verifica dell'abbonamento prima di rilasciare il loading
        await checkSubscriptionStatusService(shop.id, {
            setIsPaid,
            setSubscriptionPlan,
            setSubscriptionLoading,
        });

    } catch (error) {
        Sentry.captureException(error, {
            tags: { feature: 'session', action: 'load_initial_session' }
        });
        console.error("[Auth] Errore loadInitialSession:", error);
        // Pulizia stato in caso di errore grave
        setUser(null);
        setOrganization(null);
        setIsAuthenticated(false);
        setIsPaid(false);
        setSubscriptionPlan(null);
    } finally {
        setLoading(false);
    }
};